import { useId, useState } from 'react';
import type { ReactNode } from 'react';
import { Confirm, Rich } from '../../bits';
import { UI_LOCALES, useLocale, useT } from '../../../messages';
import type { T, UiLocale } from '../../../messages';
import type { ContentPageLimits, LocalizedText, NavEntry } from '../../../wire';
import { SiteDialog } from '../site/SiteDialog';

/**
 * **The content pages and the bar** — the fourth page shape
 * (`docs/site-section-system.md` §9.2, §9.6 decision 5), moved into the
 * builder from the old `ContentPages` panel with its rules intact:
 *
 *  - the slug box reads the served rules (`contract.content_pages`) rather
 *    than knowing them, and says while typing why a slug is refused;
 *  - titles and bar labels are the business's own words in each language it
 *    wrote, never translated and never copied across;
 *  - a bar entry is chosen from the pages this document declares, and one of
 *    any other shape (a section anchor, a category) is kept as stored;
 *  - a publish whose bar points at a page this document does not declare is
 *    never sent — the server's own rule, said sooner.
 *
 * A page's *sections* are arranged on the canvas like the home page's: the
 * page picker in the top bar switches to it.
 */

/* ------------------------------------------------------------------ */
/* The nav grammar's one string                                        */
/* ------------------------------------------------------------------ */

const PAGE_TARGET_PREFIX = 'page:';

export function pageTarget(slug: string): string {
  return `${PAGE_TARGET_PREFIX}${slug}`;
}

export function targetPage(target: string): string | null {
  return target.startsWith(PAGE_TARGET_PREFIX) ? target.slice(PAGE_TARGET_PREFIX.length) : null;
}

/** Bar entries pointing at a page this document does not declare. */
export function undeclaredPageTargets(
  nav: readonly NavEntry[],
  slugs: readonly string[],
): string[] {
  const declared = new Set(slugs);
  return nav
    .map((entry) => entry.target)
    .filter((target) => {
      const slug = targetPage(target);
      return slug !== null && !declared.has(slug);
    });
}

/* ------------------------------------------------------------------ */
/* Localized words                                                     */
/* ------------------------------------------------------------------ */

/** The operator's language if the business wrote it, else the first one they did. */
export function localizedText(value: LocalizedText, locale: UiLocale): string {
  if (typeof value === 'string') return value;
  const own = value[locale];
  if (own !== undefined && own !== '') return own;
  for (const text of Object.values(value)) if (text !== '') return text;
  return '';
}

function languagesOf(value: LocalizedText): string[] {
  if (typeof value === 'string') return [];
  const keys = Object.keys(value);
  const known = UI_LOCALES.filter((locale) => keys.includes(locale));
  return [...known, ...keys.filter((key) => !(UI_LOCALES as readonly string[]).includes(key))];
}

function setLanguage(value: LocalizedText, language: string, text: string): LocalizedText {
  if (typeof value === 'string') return text;
  return { ...value, [language]: text };
}

function addLanguage(value: LocalizedText, language: string): LocalizedText {
  if (typeof value === 'string') return { en: value, [language]: '' };
  return { ...value, [language]: '' };
}

function dropLanguage(value: LocalizedText, language: string): LocalizedText {
  if (typeof value === 'string') return value;
  const next = { ...value };
  delete next[language];
  const keys = Object.keys(next);
  const only = keys[0];
  if (keys.length === 1 && only === 'en') return next[only] ?? '';
  return next;
}

/** An input per language the business wrote in, and a button per language it has not. */
function LocalizedField({
  label,
  value,
  max,
  onChange,
}: {
  label: string;
  value: LocalizedText;
  max: number;
  onChange: (value: LocalizedText) => void;
}): ReactNode {
  const t = useT();
  const languages = languagesOf(value);
  const missing = UI_LOCALES.filter((locale) => !languages.includes(locale));
  return (
    <div className="bld-field">
      <span className="bld-field__name">{label}</span>
      {languages.length === 0 ? (
        <input
          className="bld-input"
          aria-label={label}
          value={typeof value === 'string' ? value : ''}
          maxLength={max === 0 ? undefined : max}
          onChange={(e) => onChange(e.target.value)}
        />
      ) : (
        languages.map((language) => (
          <div className="bld-row" key={language}>
            <input
              className="bld-input"
              aria-label={t('branding.inLanguage', { label, language })}
              value={typeof value === 'string' ? value : (value[language] ?? '')}
              maxLength={max === 0 ? undefined : max}
              onChange={(e) => onChange(setLanguage(value, language, e.target.value))}
            />
            <button
              type="button"
              className="linkish"
              aria-label={t('branding.dropLanguage', { label, language })}
              onClick={() => onChange(dropLanguage(value, language))}
            >
              {t('common.remove')}
            </button>
          </div>
        ))
      )}
      <div className="bld-row">
        {missing.map((language) => (
          <button
            key={language}
            type="button"
            className="linkish"
            aria-label={t('branding.addLanguage', { label, language })}
            onClick={() => onChange(addLanguage(value, language))}
          >
            {t('branding.plusLanguage', { language })}
          </button>
        ))}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* The slug box's refusals                                             */
/* ------------------------------------------------------------------ */

/**
 * Why this slug cannot be a page, in the order the rule is written — or
 * `null`, which is "nothing here refuses it" (the server still owns the
 * refusal and still gets to say it its way).
 */
export function slugRefusal(
  slug: string,
  limits: ContentPageLimits,
  taken: readonly string[],
  t: T,
): string | null {
  if (slug === '') return null;
  if (taken.includes(slug)) return t('branding.slugTaken', { slug });
  if (taken.length >= limits.pages_max) return t('branding.pagesFull', { max: limits.pages_max });
  if (slug.length > limits.slug_max) return t('branding.slugLong', { max: limits.slug_max });
  if (limits.slug_pattern !== '' && !new RegExp(limits.slug_pattern).test(slug)) {
    return t('branding.slugShape');
  }
  if (limits.reserved.includes(slug)) return t('branding.slugReserved', { slug });
  return null;
}

/* ------------------------------------------------------------------ */
/* A new page                                                          */
/* ------------------------------------------------------------------ */

export function NewPageDialog({
  limits,
  taken,
  onAdd,
  onClose,
}: {
  limits: ContentPageLimits;
  taken: readonly string[];
  onAdd: (slug: string, title: string) => void;
  onClose: () => void;
}): ReactNode {
  const t = useT();
  const slugId = useId();
  const titleId = useId();
  const [slug, setSlug] = useState('');
  const [title, setTitle] = useState('');
  const refusal = slugRefusal(slug, limits, taken, t);
  const full = taken.length >= limits.pages_max;
  const ready = slug !== '' && title !== '' && refusal === null && !full;
  return (
    <SiteDialog title={t('builder.pages.newTitle')} onClose={onClose}>
      <form
        className="bld-stack"
        onSubmit={(e) => {
          e.preventDefault();
          if (ready) onAdd(slug, title);
        }}
      >
        <div className="bld-field">
          <label htmlFor={titleId}>{t('branding.pageTitle')}</label>
          <input
            id={titleId}
            className="bld-input"
            value={title}
            maxLength={limits.title_max === 0 ? undefined : limits.title_max}
            placeholder={t('branding.pageTitlePlaceholder')}
            onChange={(e) => setTitle(e.target.value)}
          />
          <span className="bld-hint">{t('branding.pageTitleHint')}</span>
        </div>
        <div className="bld-field">
          <label htmlFor={slugId}>{t('branding.pageSlug')}</label>
          <input
            id={slugId}
            className="bld-input"
            value={slug}
            placeholder={t('branding.pageSlugPlaceholder')}
            onChange={(e) => setSlug(e.target.value)}
          />
          <span className="bld-hint">
            {t('builder.pages.slugHint', { slug: slug === '' ? '…' : slug })}
          </span>
        </div>
        {refusal === null ? null : (
          <p className="error-text" role="alert">
            <Rich text={refusal} />
          </p>
        )}
        {refusal === null && full ? (
          <p className="bld-hint">{t('branding.pagesFull', { max: limits.pages_max })}</p>
        ) : null}
        <div className="modal__actions">
          <button type="button" className="button" onClick={onClose}>
            {t('common.cancel')}
          </button>
          <button type="submit" className="button button--primary" disabled={!ready}>
            {t('branding.addPage')}
          </button>
        </div>
      </form>
    </SiteDialog>
  );
}

/* ------------------------------------------------------------------ */
/* The drawer                                                          */
/* ------------------------------------------------------------------ */

export interface PageRow {
  slug: string;
  title: LocalizedText;
}

export function PagesPanel({
  pages,
  nav,
  limits,
  onOpen,
  onTitle,
  onRemove,
  onNew,
  onNav,
}: {
  pages: readonly PageRow[];
  nav: readonly NavEntry[];
  limits: ContentPageLimits;
  onOpen: (slug: string) => void;
  onTitle: (slug: string, title: LocalizedText) => void;
  onRemove: (slug: string) => void;
  onNew: () => void;
  onNav: (nav: NavEntry[]) => void;
}): ReactNode {
  const t = useT();
  const locale = useLocale();
  const [removing, setRemoving] = useState<PageRow | null>(null);
  const linksTo = (slug: string): NavEntry[] =>
    nav.filter((entry) => targetPage(entry.target) === slug);

  return (
    <div className="bld-stack">
      <section className="bld-ins-group">
        <h4 className="bld-label">{t('branding.pages')}</h4>
        <p className="bld-hint">
          <Rich text={t('branding.pagesHint', { max: limits.pages_max })} />
        </p>
        {pages.length === 0 ? <p className="bld-hint">{t('branding.pagesNone')}</p> : null}
        <ul className="bld-pages">
          {pages.map((page) => {
            const title = localizedText(page.title, locale);
            return (
              <li key={page.slug} className="bld-pages__page">
                <div className="bld-row">
                  <strong>{title === '' ? page.slug : title}</strong>
                  <code>{t('branding.pageAddress', { slug: page.slug })}</code>
                  <span className="bld-mark">
                    {linksTo(page.slug).length === 0
                      ? t('branding.pageUnlinked')
                      : t('branding.pageLinked')}
                  </span>
                </div>
                <LocalizedField
                  label={t('builder.pages.titleOf', { slug: page.slug })}
                  value={page.title}
                  max={limits.title_max}
                  onChange={(next) => onTitle(page.slug, next)}
                />
                <div className="bld-row">
                  <button
                    type="button"
                    className="button button--sm"
                    onClick={() => onOpen(page.slug)}
                  >
                    {t('builder.pages.open')}
                  </button>
                  <button
                    type="button"
                    className="linkish"
                    aria-label={t('branding.removePage', { slug: page.slug })}
                    onClick={() => setRemoving(page)}
                  >
                    {t('common.remove')}
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
        {limits.pages_max === 0 ? null : (
          <button type="button" className="button button--sm" onClick={onNew}>
            {t('builder.pages.new')}
          </button>
        )}
      </section>

      <NavBar pages={pages} nav={nav} onChange={onNav} />

      {removing === null ? null : (
        <Confirm
          title={t('branding.removePageTitle', { slug: removing.slug })}
          consequence={`${t('branding.removePageConsequence', { slug: removing.slug })}${
            linksTo(removing.slug).length === 0
              ? ''
              : ` ${t('branding.removePageLinked', {
                  label: localizedText(linksTo(removing.slug)[0]?.label ?? '', locale),
                })}`
          }`}
          confirmLabel={t('branding.removePage', { slug: removing.slug })}
          busy={false}
          failure={null}
          onConfirm={() => {
            onRemove(removing.slug);
            setRemoving(null);
          }}
          onCancel={() => setRemoving(null)}
        />
      )}
    </div>
  );
}

/**
 * The bar: a label per entry and a target chosen from the pages this document
 * declares. A target of any other shape is kept exactly as stored and shown as
 * itself; a dangling `page:` target is shown, flagged, and blocks a publish.
 */
function NavBar({
  pages,
  nav,
  onChange,
}: {
  pages: readonly PageRow[];
  nav: readonly NavEntry[];
  onChange: (nav: NavEntry[]) => void;
}): ReactNode {
  const t = useT();
  const locale = useLocale();
  const slugs = pages.map((page) => page.slug);
  const undeclared = undeclaredPageTargets(nav, slugs);
  const patch = (index: number, entry: NavEntry): void => {
    const next = [...nav];
    next[index] = entry;
    onChange(next);
  };
  const linked = new Set(nav.map((entry) => targetPage(entry.target)).filter((s) => s !== null));
  const unlinked = pages.filter((page) => !linked.has(page.slug));

  return (
    <section className="bld-ins-group">
      <h4 className="bld-label">{t('builder.pages.bar')}</h4>
      <p className="bld-hint">
        <Rich text={t('branding.navHint')} />
      </p>
      {nav.length === 0 ? <p className="bld-hint">{t('branding.navNone')}</p> : null}
      <ol className="bld-nav">
        {nav.map((entry, index) => {
          const slug = targetPage(entry.target);
          const dangling = slug !== null && !slugs.includes(slug);
          return (
            <li key={`${entry.target}-${String(index)}`} className="bld-list__entry">
              <LocalizedField
                label={t('branding.navLabel', { n: index + 1 })}
                value={entry.label}
                max={0}
                onChange={(label) => patch(index, { ...entry, label })}
              />
              <div className="bld-field">
                <span className="bld-field__name">{t('branding.navTargetLabel')}</span>
                <select
                  className="bld-input"
                  aria-label={t('branding.navTarget', { n: index + 1 })}
                  value={entry.target}
                  onChange={(e) => patch(index, { ...entry, target: e.target.value })}
                >
                  {slug === null || dangling ? (
                    <option value={entry.target}>{entry.target}</option>
                  ) : null}
                  {pages.map((page) => (
                    <option key={page.slug} value={pageTarget(page.slug)}>
                      {localizedText(page.title, locale)}
                    </option>
                  ))}
                </select>
              </div>
              <button
                type="button"
                className="linkish"
                aria-label={t('branding.navRemove', { n: index + 1 })}
                onClick={() => onChange(nav.filter((_, i) => i !== index))}
              >
                {t('common.remove')}
              </button>
              {dangling ? (
                <p className="error-text">
                  <Rich text={t('branding.navUndeclared', { target: entry.target })} />
                </p>
              ) : null}
            </li>
          );
        })}
      </ol>
      {undeclared.length === 0 ? null : (
        <p className="bld-hint">{t('branding.navUndeclaredHint')}</p>
      )}
      <div className="bld-row">
        {unlinked.map((page) => (
          <button
            key={page.slug}
            type="button"
            className="button button--sm"
            onClick={() => onChange([...nav, { label: page.title, target: pageTarget(page.slug) }])}
          >
            {t('branding.navLink', { title: localizedText(page.title, locale) })}
          </button>
        ))}
      </div>
    </section>
  );
}
